/**
 * SAWA motion system.
 *
 * Everything here is driven by one rAF loop and a critically-damped spring
 * integrator. Springs are interruptible: retargeting mid-flight carries the
 * current velocity through instead of restarting a transition, which is what
 * makes the page feel like it has mass rather than like it is playing clips.
 *
 * Entrance states live in CSS behind `.sawa-motion`, set pre-paint in
 * sawa-theme.liquid. This module signals `window.__sawaMotionReady`; if it never
 * does, that class is dropped and every state goes inert. Nothing can stay hidden.
 */

window.__sawaMotionReady = true;

const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');
const FINE = matchMedia('(pointer: fine)');
const EPS = 0.0001;

/** Semi-implicit Euler spring. Heavier mass = more lag = further back in depth. */
class Spring {
  constructor({ stiffness = 0.08, damping = 0.78, mass = 1, value = 0 } = {}) {
    this.k = stiffness;
    this.d = damping;
    this.m = mass;
    this.value = value;
    this.target = value;
    this.velocity = 0;
  }

  step() {
    const force = ((this.target - this.value) * this.k) / this.m;
    this.velocity = (this.velocity + force) * this.d;
    this.value += this.velocity;
    const settled = Math.abs(this.velocity) < EPS && Math.abs(this.target - this.value) < EPS;
    if (settled) this.value = this.target;
    return !settled;
  }
}

/** One loop for the whole page. Subscribers are functions returning "still busy". */
const ticker = {
  jobs: new Set(),
  running: false,
  add(fn) {
    this.jobs.add(fn);
    this.start();
  },
  start() {
    if (this.running) return;
    this.running = true;
    const tick = () => {
      let busy = false;
      for (const job of this.jobs) busy = job() || busy;
      if (busy) requestAnimationFrame(tick);
      else this.running = false;
    };
    requestAnimationFrame(tick);
  },
};

/**
 * Scroll VELOCITY, not scroll position. Position-mapped parallax is the flat
 * "beginner" tell; reacting to how fast the reader is moving is what gives the
 * page weight — it stretches under acceleration and recoils when you stop.
 */
function createScrollVelocity() {
  const spring = new Spring({ stiffness: 0.12, damping: 0.72 });
  let last = window.scrollY;
  let raw = 0;

  addEventListener(
    'scroll',
    () => {
      const y = window.scrollY;
      raw = Math.max(-140, Math.min(140, y - last));
      last = y;
      spring.target = raw;
      ticker.start();
    },
    { passive: true }
  );

  ticker.add(() => {
    // Bleed the impulse away so the spring recoils to rest rather than holding.
    spring.target *= 0.86;
    if (Math.abs(spring.target) < EPS) spring.target = 0;
    return spring.step();
  });

  return spring;
}

/**
 * Depth. Each layer has its own mass, so under a fast scroll they separate and
 * then re-converge as the springs settle — parallax that behaves like matter.
 */
function initDepth(root, velocity) {
  const layers = [...root.querySelectorAll('[data-parallax]')].map((el) => {
    const rate = parseFloat(el.dataset.parallax) || 0;
    return {
      el,
      rate,
      drift: parseFloat(el.dataset.drift) || 0,
      // lighter elements chase harder; heavier ones lag and overshoot
      offset: new Spring({ stiffness: 0.1 - Math.abs(rate) * 0.25, damping: 0.82, mass: 1 + Math.abs(rate) * 6 }),
      shear: new Spring({ stiffness: 0.09, damping: 0.75, mass: 1 + Math.abs(rate) * 4 }),
    };
  });
  if (!layers.length) return null;

  const pointer = { x: new Spring({ stiffness: 0.05, damping: 0.86, mass: 1.4 }), y: new Spring({ stiffness: 0.05, damping: 0.86, mass: 1.4 }) };

  if (FINE.matches) {
    root.addEventListener(
      'pointermove',
      (e) => {
        const r = root.getBoundingClientRect();
        pointer.x.target = (e.clientX - r.left) / r.width - 0.5;
        pointer.y.target = (e.clientY - r.top) / r.height - 0.5;
        ticker.start();
      },
      { passive: true }
    );
    root.addEventListener('pointerleave', () => {
      pointer.x.target = 0;
      pointer.y.target = 0;
      ticker.start();
    });
  }

  // The copy travels with the page and dissolves as the section leaves, so the
  // monument is handed off rather than cut. Starts late so nothing fades while
  // it is still being read.
  const exiting = root.querySelector('[data-parallax-exit]');

  ticker.add(() => {
    const rect = root.getBoundingClientRect();
    const onScreen = rect.bottom > 0 && rect.top < innerHeight;
    let busy = pointer.x.step() | pointer.y.step();

    if (onScreen) {
      const travelled = -rect.top;

      if (exiting) {
        const progress = Math.min(1, Math.max(0, travelled / Math.max(1, rect.height)));
        const out = Math.min(1, Math.max(0, (progress - 0.35) / 0.65));
        exiting.style.setProperty('--sawa-exit', out.toFixed(3));
      }
      for (const layer of layers) {
        layer.offset.target = travelled * layer.rate;
        // shear scales with the layer's own lag, so depth reads in the distortion too
        layer.shear.target = velocity.value * layer.rate * 0.14;
        busy = layer.offset.step() | layer.shear.step() | busy;

        const dx = pointer.x.value * layer.drift;
        const dy = pointer.y.value * layer.drift * 0.55;
        layer.el.style.setProperty('--sawa-py', `${(layer.offset.value + dy).toFixed(2)}px`);
        layer.el.style.setProperty('--sawa-dx', `${dx.toFixed(2)}px`);
        layer.el.style.setProperty('--sawa-shear', `${layer.shear.value.toFixed(3)}deg`);
        // stretch along travel, the way a heavy object smears when thrown
        layer.el.style.setProperty('--sawa-stretch', `${(1 + Math.abs(layer.shear.value) * 0.02).toFixed(4)}`);
      }
    }
    return !!busy;
  });

  return layers;
}

/**
 * The monument is cloth, not stone: an SVG displacement map ripples the
 * letterform on arrival and under scroll velocity, then resolves to zero so it
 * costs nothing at rest.
 */
function initCloth(root, velocity) {
  const disp = root.querySelector('[data-cloth-displacement]');
  if (!disp || !FINE.matches || innerWidth < 750) return;

  // Rest state is zero displacement -- a crisp letterform. The ripple only ever
  // rides scroll velocity, so a stalled frame leaves the page correct, never smeared.
  const amp = new Spring({ stiffness: 0.09, damping: 0.82, mass: 1.4, value: 0 });
  let last = 0;

  ticker.add(() => {
    amp.target = Math.min(14, Math.abs(velocity.value) * 0.22);
    const busy = amp.step();
    const next = Math.max(0, amp.value);
    if (Math.abs(next - last) > 0.05) {
      disp.setAttribute('scale', next.toFixed(2));
      last = next;
    }
    return busy;
  });
}

/** Split into per-glyph spans so weight and rise can travel as a wave. */
function splitGlyphs(root) {
  if (!root || root.dataset.sawaSplit === 'true') return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);

  for (const node of nodes) {
    if (!node.textContent.trim()) continue;
    const frag = document.createDocumentFragment();
    for (const word of node.textContent.split(/(\s+)/)) {
      if (!word) continue;
      if (/^\s+$/.test(word)) {
        frag.appendChild(document.createTextNode(word));
        continue;
      }
      const mask = document.createElement('span');
      mask.className = 'sawa-w';
      for (const ch of [...word]) {
        const inner = document.createElement('span');
        inner.className = 'sawa-w__i';
        inner.textContent = ch;
        mask.appendChild(inner);
      }
      frag.appendChild(mask);
    }
    node.parentNode.replaceChild(frag, node);
  }

  const glyphs = [...root.querySelectorAll('.sawa-w__i')];
  glyphs.forEach((el, i) => {
    // a wave, not a uniform stagger
    el.style.setProperty('--sawa-i', i);
    el.style.setProperty('--sawa-d', `${(0.028 * i).toFixed(3)}s`);
  });
  const accents = glyphs.filter((el) => el.closest('em, i'));
  (accents.length ? accents : glyphs.slice(-1)).forEach((el) => el.classList.add('sawa-w__i--accent'));
  root.dataset.sawaSplit = 'true';
}

function observe(targets) {
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      }
    },
    { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
  );
  targets.forEach((t) => io.observe(t));
}

function initBolt(root) {
  const groups = [...root.querySelectorAll('.variant-option--buttons')];
  if (!groups.length) return;
  const place = (group) => {
    const checked = group.querySelector('input:checked');
    const label = checked && checked.closest('.variant-option__button-label');
    if (!label) return;
    group.style.setProperty('--sawa-cut-x', `${label.offsetLeft}px`);
    group.style.setProperty('--sawa-cut-y', `${label.offsetTop + label.offsetHeight - 1}px`);
    group.style.setProperty('--sawa-cut-s', `${label.offsetWidth / 100}`);
  };
  const placeAll = () => groups.forEach(place);
  groups.forEach((g) => {
    g.addEventListener('change', () => place(g));
    if (document.fonts?.ready) document.fonts.ready.then(() => place(g));
  });
  if ('ResizeObserver' in window) {
    const ro = new ResizeObserver(placeAll);
    groups.forEach((g) => ro.observe(g));
  }
  document.addEventListener('variant:update', () => requestAnimationFrame(placeAll));
  placeAll();
}



/**
 * The phrasebook. Every piece in this collection is named after a saying, and
 * the About page says the label exists to carry "the nods and winks that others
 * in our communities just get". So the index teaches them.
 *
 * Matched from the product handle for now; this belongs in a `sawa.phrase`
 * metafield so the copy lives with the merchandising instead of here.
 */
const PHRASES = [
  ['3adi', 'عادي', '3adi — normal. fine. no problem.'],
  ['3ala', 'على راسي', '3ala rasi — on my head. with pleasure.'],
  ['habibi', 'حبيبي إنت', 'habibi inta — you’re my dear.'],
  ['shu-fi', 'شو في ما في', 'shu fi ma fi — what’s up, what’s new.'],
  ['shufi', 'شو في ما في', 'shu fi ma fi — what’s up, what’s new.'],
  ['teta', 'تيتا', 'teta approved — grandmother says yes.'],
];
const PHRASE_DEFAULT = ['سوا', 'sawa — together.'];

function initPhrasebook() {
  const cards = document.querySelectorAll('.sawa-collection .product-grid .product-card');
  cards.forEach((card) => {
    const link = card.querySelector('a[href*="/products/"]');
    const handle = (link ? link.getAttribute('href') : '').toLowerCase();
    const hit = PHRASES.find(([key]) => handle.includes(key));
    const [arabic, meaning] = hit ? [hit[1], hit[2]] : PHRASE_DEFAULT;

    const media = card.querySelector('.product-media-container') || card.querySelector('.product-media');
    if (media) media.setAttribute('data-sawa-phrase', arabic);

    const title = card.querySelector('.product-title');
    if (title) title.setAttribute('data-sawa-meaning', meaning);
  });
}


/**
 * One progress value drives the whole first page. The monument is fixed behind
 * the acts and every transform on it -- scale, drift, dimming, the rake -- reads
 * off this single number, spring-smoothed so the move is continuous rather than
 * locked frame-to-frame with the scrollbar.
 */
function initStage() {
  const stage = document.querySelector('.sawa-stage');
  if (!stage) return;

  const p = new Spring({ stiffness: 0.1, damping: 0.82, mass: 1.2 });
  const acts = document.querySelector('.content-for-layout');

  const measure = () => {
    if (!acts) return 0;
    // progress across the acts, ending as the last one leaves
    const span = Math.max(1, acts.offsetHeight - innerHeight);
    return Math.min(1, Math.max(0, window.scrollY / span));
  };

  const onScroll = () => {
    p.target = measure();
    ticker.start();
  };

  addEventListener('scroll', onScroll, { passive: true });
  addEventListener('resize', onScroll, { passive: true });

  ticker.add(() => {
    const busy = p.step();
    stage.style.setProperty('--p', p.value.toFixed(4));
    return busy;
  });

  onScroll();
}

function init() {
  const hero = document.querySelector('.sawa-hero') || document.querySelector('.sawa-photo--mark');
  const arrivals = document.querySelector('.sawa-arrivals');
  const info = document.querySelector('.product-information');
  const grid = document.querySelector('.sawa-collection .main-collection-grid');
  if (grid) grid.querySelectorAll('.product-card').forEach((el, i) => el.style.setProperty('--sawa-delay', `${(i % 8) * 0.06}s`));

  if (hero) splitGlyphs(hero.querySelector('.sawa-hero__headline'));
  if (hero) hero.querySelectorAll('.sawa-hero__frame').forEach((el, i) => el.style.setProperty('--sawa-delay', `${0.45 + i * 0.13}s`));
  if (arrivals) arrivals.querySelectorAll('.sawa-card').forEach((el, i) => el.style.setProperty('--sawa-delay', `${i * 0.09}s`));

  if (REDUCED.matches) {
    document.documentElement.classList.remove('sawa-motion');
    [hero, arrivals, info].forEach((el) => el && el.classList.add('is-in'));
    if (info) initBolt(info);
    return;
  }

  const velocity = createScrollVelocity();
  initPhrasebook();
  initStage();

  if (hero) {
    requestAnimationFrame(() => requestAnimationFrame(() => hero.classList.add('is-in')));
    initDepth(hero, velocity);
    initCloth(hero, velocity);
  }
  if (arrivals) {
    observe([arrivals]);
    initDepth(arrivals, velocity);
  }
  if (info) {
    // Arm only once we have the element in hand: the hidden state must never
    // exist unless something is guaranteed to remove it.
    info.classList.add('sawa-armed');
    requestAnimationFrame(() => requestAnimationFrame(() => info.classList.add('is-in')));
    initBolt(info);
  }
  // The collection grid reveals itself in CSS; JS must never gate its visibility.
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
